import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton, Typography, message } from "antd";
import dayjs from "dayjs";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import { UiButton, ActionIconButton, EditIcon } from "../../ui";
import { ListScreen, DetailCard } from "../common/ListScreen";
import CgIcon from "../common/CgIcon";
import { UserContext } from "../../../context/UserContext";
import { boardTheme } from "../Dashboard/heroTheme";
import { formatMoney } from "../common/money";
import {
  getPharmacyMedicineApi,
  getPharmacyMedicineAllBatchesApi,
} from "../../../services/pharmacyMedicines.services";
import BatchEditModal from "./BatchEditModal";
import MedicineFormModal from "./MedicineFormModal";
import MedicineOrdersModal from "./MedicineOrdersModal";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";
import styles from "./medicineDetail.module.css";

/*
 * One medicine, on one screen - ported from the approved mockup.
 *
 * The catalogue row says how much there is; this says everything else: what it
 * costs, what it earns, how it is taxed, how it is packed, and which batches
 * are running out. It opens on a band carrying the figures that decide whether
 * anything needs doing, and the detail sits underneath for when it does.
 */

const DRUG_CLASSIFICATION_LABEL = {
  normal: "Normal",
  schedule_x: "Schedule X",
  habit_forming: "Habit Forming",
};

/* Money without the paise - these are figures to take in at a glance, and a
   column of ".00" is noise. */
const inr0 = (value) =>
  `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Math.round(Number(value) || 0)
  )}`;

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const has = (value) =>
  value !== null && value !== undefined && value !== "" && num(value) !== 0;

const date = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "-");

/* How long a batch has left, in days. Negative means it has gone. */
const daysLeft = (expiry) =>
  expiry ? dayjs(expiry).startOf("day").diff(dayjs().startOf("day"), "day") : null;

/*
 * The mockup colours a batch by how close it is: red inside three weeks, amber
 * inside six, green beyond. The bar reads against a 60-day window, so a batch
 * with longer than that left simply shows full.
 */
const expiryTone = (days) => {
  if (days === null) return "var(--o-n-300)";
  if (days < 21) return "#F04438";
  if (days < 45) return "#EAAA5E";
  return "#12B76A";
};

const Row = ({ label, children }) => (
  <div>
    <span className={styles.k}>{label}</span>
    {children}
  </div>
);

/* A field with nothing in it says so quietly - a dash that reads as "not
   set" rather than as a value of its own. */
const Value = ({ value, money }) => {
  const empty = value === null || value === undefined || value === "";
  return empty ? (
    <span className={`${styles.v} ${styles.dim}`}>—</span>
  ) : (
    <span className={styles.v}>{money ? formatMoney(value) : value}</span>
  );
};

const Chip = ({ label, on, warn }) => (
  <span
    className={`${styles.chip} ${on ? (warn ? styles.chipWarn : styles.chipOn) : ""}`}
  >
    <i />
    {label}
  </span>
);

const GstChip = ({ label, value }) =>
  has(value) ? (
    <span className={`${styles.chip} ${styles.chipGst}`}>
      {label} <b>{num(value)}%</b>
    </span>
  ) : (
    <span className={styles.chip}>
      {label} <b>0%</b>
    </span>
  );

const MedicineDetailPage = () => {
  const { medicineId } = useParams();
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);
  const [medicine, setMedicine] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batchEditTarget, setBatchEditTarget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);

  const loadBatches = useCallback(() => {
    if (!medicineId) return Promise.resolve();
    return getPharmacyMedicineAllBatchesApi(medicineId)
      .then((res) => setBatches(res?.data || []))
      .catch(() => message.error("Failed to load batches"));
  }, [medicineId]);

  const loadMedicine = useCallback(
    () =>
      getPharmacyMedicineApi(medicineId)
        .then((res) => setMedicine(res?.data))
        .catch(() => {
          setMedicine(null);
          message.error("Failed to load medicine");
        }),
    [medicineId]
  );

  useEffect(() => {
    if (!medicineId) return;
    setLoading(true);
    Promise.all([loadMedicine(), loadBatches()]).finally(() => setLoading(false));
  }, [medicineId, loadMedicine, loadBatches]);

  /*
   * What the band reports. Stock is valued at Rate A - the price it is
   * expected to leave at - and the margin is measured against the purchase
   * rate, which a medicine that has never been bought in does not have.
   */
  const figures = useMemo(() => {
    const stock = num(medicine?.current_stock);
    const limit = num(medicine?.stockout_limit);
    const rateA = num(medicine?.rate_a);
    const purchase = num(medicine?.purchase_rate);
    const marginUnit = purchase ? rateA - purchase : null;
    const marginPercent =
      purchase && rateA ? Math.round(((rateA - purchase) / rateA) * 1000) / 10 : null;

    return {
      stock,
      limit,
      rateA,
      marginUnit,
      marginPercent,
      value: stock * rateA,
      headroom:
        stock <= limit
          ? `${limit - stock || "at"} below the re-order level`
          : `${stock - limit} above the re-order level`,
    };
  }, [medicine]);

  /* Soonest to expire first - the batches worth doing something about lead. */
  const sortedBatches = useMemo(
    () =>
      [...batches].sort((a, b) => {
        const left = daysLeft(a.expiry);
        const right = daysLeft(b.expiry);
        if (left === null) return 1;
        if (right === null) return -1;
        return left - right;
      }),
    [batches]
  );

  const page = (children) => (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
    >
      {/* The organisation's own ramp, the one the board's band is painted
          with - this screen re-themes with the org exactly as it does. */}
      <div
        style={{
          padding: "0 24px 24px",
          ...boardTheme(userDetails?.activeOrg?.color),
        }}
      >
        <ListScreen>{children}</ListScreen>
      </div>
    </CustomLayout>
  );

  if (loading) return page(<Skeleton active />);
  if (!medicine)
    return page(<Typography.Text type="secondary">Medicine not found.</Typography.Text>);

  return (
    <>
      {page(
        <>
          {/* Edit sits at the top right rather than under the band, which put a
              primary button two scrolls above the batches it pushed down. */}
          <div className={styles.top}>
            <button
              type="button"
              className={styles.back}
              onClick={() => navigate("/pharmacy/medicines")}
            >
              <CgIcon name="back" size={15} stroke={2.4} />
              All medicines
            </button>
            {/* The two actions stay together against the right edge - the row
                spreads its children apart, and a third one loose in it would
                have left Orders History stranded in the middle. */}
            <div className={styles.topActions}>
              {/* Where this medicine has been going: every order it has been
                  put on, beside the way to change the medicine itself. */}
              <UiButton variant="secondary" onClick={() => setOrdersOpen(true)}>
                <CgIcon name="ledger" size={16} stroke={2} />
                Orders History
              </UiButton>
              <UiButton onClick={() => setEditOpen(true)}>
                <EditIcon /> Edit medicine
              </UiButton>
            </div>
          </div>

          {/* ---- the band ---- */}
          <div className={styles.hero}>
            <div className={styles.heroIn}>
              <div style={{ minWidth: 0 }}>
                <div className={styles.name}>{medicine.name}</div>
                <div className={styles.salt}>
                  {[medicine.drug1_name, medicine.item_type_name, medicine.company_name]
                    .filter(Boolean)
                    .join(" · ") || "No salt, type or company on file"}
                </div>
                <div className={styles.tags}>
                  <span className={styles.tag}>
                    <CgIcon name="check" size={12} stroke={2.6} />
                    {medicine.is_active ? "Active" : "Inactive"}
                  </span>
                  {medicine.hsn_or_sac ? (
                    <span className={styles.tag}>HSN {medicine.hsn_or_sac}</span>
                  ) : null}
                  {medicine.category_name ? (
                    <span className={styles.tag}>{medicine.category_name}</span>
                  ) : null}
                  {medicine.rack_no ? (
                    <span className={styles.tag}>Rack {medicine.rack_no}</span>
                  ) : null}
                  {medicine.drug_classification &&
                  medicine.drug_classification !== "normal" ? (
                    <span className={styles.tag}>
                      {DRUG_CLASSIFICATION_LABEL[medicine.drug_classification] ||
                        medicine.drug_classification}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className={styles.metrics}>
                <div>
                  <div className={styles.mlabel}>On hand</div>
                  <div className={styles.mval}>{figures.stock}</div>
                  <div className={styles.msub}>{figures.headroom}</div>
                </div>
                <div>
                  <div className={styles.mlabel}>Stock value</div>
                  <div className={styles.mval}>{inr0(figures.value)}</div>
                  <div className={styles.msub}>at Rate A</div>
                </div>
                <div>
                  <div className={styles.mlabel}>MRP</div>
                  <div className={styles.mval}>{inr0(medicine.mrp)}</div>
                  <div className={styles.msub}>Rate A {inr0(figures.rateA)}</div>
                </div>
                <div>
                  <div className={styles.mlabel}>Margin</div>
                  <div className={styles.mval}>
                    {figures.marginPercent === null ? "—" : `${figures.marginPercent}%`}
                  </div>
                  <div className={styles.msub}>
                    {figures.marginUnit === null
                      ? "no purchase rate"
                      : `${inr0(figures.marginUnit)} a unit`}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ---- pricing, with tax and the switches folded in under it ---- */}
          <div className={styles.grid}>
            <DetailCard title="Pricing">
              <div className={styles.kv}>
                <Row label="MRP">
                  <Value value={medicine.mrp} money />
                </Row>
                <Row label="Purchase rate">
                  <Value value={has(medicine.purchase_rate) ? medicine.purchase_rate : null} money />
                </Row>
                <Row label="Rate A">
                  <Value value={medicine.rate_a} money />
                </Row>
                <Row label="Rate B">
                  <Value value={has(medicine.rate_b) ? medicine.rate_b : null} money />
                </Row>
                <Row label="Rate C">
                  <Value value={has(medicine.rate_c) ? medicine.rate_c : null} money />
                </Row>
                <Row label="Loose price">
                  <Value value={has(medicine.loose_price) ? medicine.loose_price : null} money />
                </Row>
                <Row label="HSN / SAC">
                  <Value value={medicine.hsn_or_sac} />
                </Row>
                {/* Margin reads as a movement rather than as another figure
                    in the column. */}
                <Row label="Margin a unit">
                  {figures.marginUnit === null ? (
                    <span className={`${styles.trend} ${styles.trendFlat}`}>
                      no purchase rate yet
                    </span>
                  ) : (
                    <span
                      className={`${styles.trend} ${
                        figures.marginUnit >= 0 ? "" : styles.trendDown
                      }`}
                    >
                      <CgIcon
                        name="chev"
                        size={13}
                        stroke={2.4}
                        style={{
                          transform: figures.marginUnit >= 0 ? "rotate(180deg)" : "none",
                        }}
                      />
                      {figures.marginPercent}%<em>{inr0(figures.marginUnit)} a unit</em>
                    </span>
                  )}
                </Row>
              </div>

              {/* Four rates on one line - stacked they cost five rows and push
                  the batches off the screen. */}
              <div className={`${styles.chips} ${styles.gsts}`}>
                <GstChip label="SGST" value={medicine.sgst} />
                <GstChip label="CGST" value={medicine.cgst} />
                <GstChip label="IGST" value={medicine.igst} />
                <GstChip label="Cess" value={medicine.cess} />
              </div>

              <div className={styles.chips}>
                <Chip label="Intra-state GST" on={medicine.is_local_gst_applicable} />
                <Chip label="Central GST" on={medicine.is_central_gst_applicable} />
                <Chip label="Narcotic" on={medicine.is_narcotic} warn />
                <Chip label="Schedule H" on={medicine.is_schedule_h} warn />
                <Chip label="Schedule H1" on={medicine.is_schedule_h1} warn />
                <Chip label="Negative stock" on={medicine.allow_negative_stock} />
                <Chip label="Rate override" on={medicine.rate_override_allowed} />
                <Chip label="Discount allowed" on={medicine.is_discount_applicable} />
              </div>
            </DetailCard>

            {/* ---- and, in the slot tax used to hold, how it is made up ---- */}
            <DetailCard title="How it is packed">
              <div className={styles.kv}>
                <Row label="Packing">
                  <Value value={medicine.packing} />
                </Row>
                <Row label="Unit">
                  <Value value={medicine.unit} />
                </Row>
                <Row label="Strip conversion">
                  <Value value={medicine.strip_conversion} />
                </Row>
                <Row label="Rack">
                  <Value value={medicine.rack_no} />
                </Row>
                <Row label="Product type">
                  <Value value={medicine.product_type} />
                </Row>
                <Row label="Item type">
                  <Value value={medicine.item_type_name} />
                </Row>
                <Row label="Company">
                  <Value value={medicine.company_name} />
                </Row>
                <Row label="Category">
                  <Value value={medicine.category_name} />
                </Row>
                <Row label="Re-order level">
                  <Value value={figures.limit} />
                </Row>
                <Row label="Min / Max">
                  {medicine.minimum_qty || medicine.maximum_qty ? (
                    <span className={styles.v}>
                      {medicine.minimum_qty || "—"} / {medicine.maximum_qty || "—"}
                    </span>
                  ) : (
                    <span className={`${styles.v} ${styles.dim}`}>not set</span>
                  )}
                </Row>
                <Row label="Classification">
                  <Value
                    value={
                      DRUG_CLASSIFICATION_LABEL[medicine.drug_classification] ||
                      medicine.drug_classification
                    }
                  />
                </Row>
                <Row label="Search keyword">
                  <Value value={medicine.fast_search_keyword} />
                </Row>
              </div>
            </DetailCard>
          </div>

          {/* ---- batches, the full width of the page ---- */}
          <div className={`${styles.grid} ${styles.one}`}>
            <DetailCard title="Batches" count={sortedBatches.length}>
              {sortedBatches.length ? (
                sortedBatches.map((batch) => {
                  const days = daysLeft(batch.expiry);
                  const tone = expiryTone(days);
                  return (
                    <div className={styles.exp} key={batch.id}>
                      <span className={styles.expIco}>
                        <CgIcon name="clock" size={15} stroke={2} />
                      </span>
                      <span className={styles.expMain}>
                        <b>Batch {batch.batch_number || "—"}</b>
                        <i>
                          {num(batch.available_qty)} units · expires {date(batch.expiry)}
                          {has(batch.mrp) ? ` · MRP ${formatMoney(batch.mrp)}` : ""}
                          {has(batch.selling_price)
                            ? ` · Rate A ${formatMoney(batch.selling_price)}`
                            : ""}
                        </i>
                      </span>
                      {/* The bar reads against a 60-day window; anything
                          further out simply shows full. */}
                      <span className={styles.expBar}>
                        <span
                          style={{
                            width:
                              days === null
                                ? "100%"
                                : `${Math.max(4, Math.min(100, Math.round((days / 60) * 100)))}%`,
                            background: tone,
                          }}
                        />
                      </span>
                      <span className={styles.expDays} style={{ color: tone }}>
                        {days === null
                          ? "no expiry"
                          : days < 0
                          ? "expired"
                          : `${days} days`}
                      </span>
                      <ActionIconButton
                        aria-label="Edit batch"
                        onClick={() => setBatchEditTarget(batch)}
                      >
                        <EditIcon />
                      </ActionIconButton>
                    </div>
                  );
                })
              ) : (
                <div className={styles.empty}>
                  No batch of this medicine has been purchased in yet.
                </div>
              )}
            </DetailCard>
          </div>
        </>
      )}

      {batchEditTarget && (
        <BatchEditModal
          open={Boolean(batchEditTarget)}
          setOpen={() => setBatchEditTarget(null)}
          medicineId={medicineId}
          batch={batchEditTarget}
          onSaved={loadBatches}
        />
      )}

      {editOpen && (
        <MedicineFormModal
          open={editOpen}
          setOpen={setEditOpen}
          editData={medicine}
          refreshPage={loadMedicine}
        />
      )}

      {ordersOpen && (
        <MedicineOrdersModal
          open={ordersOpen}
          onClose={() => setOrdersOpen(false)}
          medicine={medicine}
        />
      )}
    </>
  );
};

export default MedicineDetailPage;
